import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactJson from 'react-json-view';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

// @material-ui/core components
import withStyles from '@material-ui/core/styles/withStyles';

// @material-ui/icons
import PeopleOutline from '@material-ui/icons/PeopleOutline';
import CheckMark from 'assets/img/Checkmark.svg';
import Close from 'assets/img/Close.svg';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import NavPills from 'components/NavPills/NavPills';
import ReadOnlyField from 'components/ReadOnlyField/ReadOnlyField';
import AuditLog from 'components/AuditLog/AuditLog';
import ReactTable from 'components/ReactTable/ReactTable';
import CardIcon from 'components/Card/CardIcon';

import DateFormatResolver, { fullDateUsFormat } from 'resolvers/date-format.resolver';
import { setPageHeaderTextActionCreator } from 'plugins/store/reducers/common.reducer';
import {
	getPushNotification,
} from 'services/push-notification.service';
import layoutNames from 'utils/enums/layout-names.enum';

const styles = {
	'details-container': {
		borderBottom: '1px solid #000',
	},
	receivedByContainer: {
		marginTop: '50px',
	},
	cardHeader: {
		position: 'absolute',
	},
	cardBody: {
		paddingTop: '0',
	},
};

const INIT_CALL_STATE = 'INIT_CALL';
const CREATED_BY_CAF = 'CAF System';

class PushNotificationDetails extends Component {
	constructor(props) {
		super(props);

		this.onNavPillsChange = this.onNavPillsChange.bind(this);
	}

	state = {
		pushNotification: {
			sentResult: [{
				sent: false,
			}],
		},
		activeTabIndex: 0,
		receivedByTable: {
			columns: [
				{
					Header: 'Received at',
					accessor: 'at',
					Cell: ({ value }) => DateFormatResolver.resolve(value, fullDateUsFormat),
				},
				{
					Header: 'Connection',
					accessor: 'connection',
				},
				{
					Header: 'By Token',
					accessor: 'byToken',
				},
				{
					Header: 'Elapsed Time',
					accessor: 'elapsedTime',
					Cell: ({ row }) => this.calculateElapsedTime(row.cells[0]?.value),
				},
			],
			data: [],
			loading: false,
			pageCount: 0,
			totalRecordsCount: 0,
		},
	};

	componentDidMount() {
		this.getRecordDetails();
	}

	componentWillUnmount() {
		this.props.setPageHeaderText('');
	}

	onNavPillsChange(state) {
		this.setState({
			activeTabIndex: state.activeTabIndex || 0,
		});
	}

	async getRecordDetails() {
		try {
			const id = this.props.match?.params?.id;

			if (id) {
				this.setState({
					isLoading: true,
				});

				const pushNotification = await getPushNotification(id);
				this.props.setPageHeaderText(pushNotification?.type || '');
				this.setState((state) => ({
					isLoading: false,
					pushNotification: pushNotification,
					receivedByTable: {
						...state.receivedByTable,
						data: pushNotification.receivedBy,
					},
				}));
			}
		} catch (error) {
			console.log(error);
		}
	}

	calculateElapsedTime(endTime) {
		const startTime = this.state.pushNotification?.sentAt;
		if (!startTime || !endTime) {
			return null;
		}

		let elapsedTime;
		try {
			elapsedTime = (new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000;
		} catch (error) {
			console.log('Cannot parse notification start/end date and time');
		}

		return elapsedTime ? `${DateFormatResolver.resolveTimeFromSecs(Math.round(elapsedTime))}` : null;
	}

	render() {
		const { classes } = this.props;
		const {
			type,
			phoneNumber,
			customerPhoneNumber,
			sentBy,
			sentAt,
			sentToTokens,
			sentResult,
			isSent,
			sentResponse,
			createdBy,
			createdAt,
			modifiedBy,
			updatedAt,
			requestArrivalTime,
			callStartedAtByCaf,
			callEndedAtByCaf,
		} = this.state.pushNotification;

		const fetchData = () => { };

		const isInit = type === INIT_CALL_STATE;

		return (
			<GridContainer>
				<GridItem xs={12} sm={12} md={12}>
					<Card>
						<CardBody>
							<NavPills
								color="warning"
								tabs={[
									{
										tabButton: 'Details',
										tabContent: (
											<div className="details-container">
												<div>
													<ReadOnlyField
														label="Type"
														value={type || ''}
													/>
													<ReadOnlyField
														label="Calling from"
														value={
															phoneNumber || ''
														}
													/>
													<ReadOnlyField
														label="Calling to"
														value={
															customerPhoneNumber || ''
														}
													/>
													<ReadOnlyField
														label="Sent by"
														value={
															sentBy ||
															'Sevis Manager'
														}
													/>
													{
														createdBy === CREATED_BY_CAF &&
														<>
															<ReadOnlyField
																label={isInit ? 'Call Started at by CAF' : 'Call Ended at by CAF'}
																value={DateFormatResolver.resolve(isInit ? callStartedAtByCaf : callEndedAtByCaf, fullDateUsFormat)}
															/>
															<ReadOnlyField
																label="Request received by Portal at"
																value={DateFormatResolver.resolve(requestArrivalTime, fullDateUsFormat)}
															/>
														</>
													}
													<ReadOnlyField
														label="Sent at"
														value={DateFormatResolver.resolve(sentAt, fullDateUsFormat)}
													/>
													<ReadOnlyField
														label="Sent to Tokens"
														value={
															sentToTokens || ''
														}
													/>
													<ReadOnlyField
														label="Sent"
														children={
															isSent
																? <img src={CheckMark} alt="Check Mark" />
																: <img src={Close} alt="Close" />
														}
													/>
													<ReadOnlyField
														label="Sent Error"
														value={
															sentResult[0]
																.error || ''
														}
													/>
													<ReadOnlyField
														label="Sent Result"
														value={
															JSON.stringify(
																sentResponse
															) || ''
														}
													/>
												</div>
											</div>
										),
									},
									{
										tabButton: 'Sent Notification',
										tabContent: (
											<div>
												<ReactJson
													src={
														this.state
															.pushNotification
															?.pushNotification
													}
												/>
											</div>
										),
									},
									{
										tabButton: 'Audit Log',
										tabContent: (
											<AuditLog
												createdBy={createdBy}
												createdAt={createdAt}
												modifiedBy={modifiedBy}
												modifiedAt={updatedAt}
											/>
										),
									},
								]}
								onNavPillsChange={this.onNavPillsChange}
							/>
						</CardBody>
					</Card>

					{this.state.activeTabIndex === 0 && (
						<Card className={classes.receivedByContainer}>
							<CardHeader style={styles.cardHeader} color="primary" icon>
								<CardIcon color="primary">
									<PeopleOutline />
								</CardIcon>
							</CardHeader>
							<CardBody style={styles.cardBody}>
								<GridContainer>
									<GridItem xs={12} sm={12} md={12}>
										<ReactTable
											title={{
												text: 'Received by',
												withCardIcon: true,
											}}
											isFrontEndData={true}
											clickableRows={false}
											rowsWithSelection={false}
											columns={
												this.state.receivedByTable
													.columns
											}
											data={
												this.state.receivedByTable.data
											}
											fetchData={fetchData}
											loading={
												this.state.receivedByTable
													.loading
											}
											pageCount={
												this.state.receivedByTable
													.pageCount
											}
											totalRecordsCount={
												this.state.receivedByTable
													.totalRecordsCount
											}
											detailsPath={
												`${layoutNames.portal}/push-notifications/details`
											}
										></ReactTable>
									</GridItem>
								</GridContainer>
							</CardBody>
						</Card>
					)}
				</GridItem>
			</GridContainer>
		);
	}
}

PushNotificationDetails.propTypes = {
	classes: PropTypes.any,
	setPageHeaderText: PropTypes.func,
	location: PropTypes.object,
	match: PropTypes.any,
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
	setPageHeaderText: bindActionCreators(
		setPageHeaderTextActionCreator,
		dispatch
	),
});

export default withStyles(styles)(
	connect(mapStateToProps, mapDispatchToProps)(PushNotificationDetails)
);
